/**
 * LegalGate — post-login compliance gate.
 *
 * Forces the user to scroll through each of the four legal documents (Terms,
 * Privacy, Risk, NDPR) before the trading UI becomes available. We cannot
 * *prove* a user has read the text, but we can require:
 *   1. Each document tab is opened.
 *   2. The scrollable content area is scrolled to within 2% of the bottom.
 *   3. The user explicitly clicks the per-document acknowledge button.
 *
 * Acknowledgements are timestamped to localStorage under `nigastock.legal.v1`
 * and persisted server-side by the user-profile service on next API call.
 *
 * Document version is embedded in the localStorage key; bumping the version
 * forces everyone to re-acknowledge on next login (compliance refresh).
 */
import { useEffect, useRef, useState } from 'react';

const KEY = 'nigastock.legal.v2';
const DOCS = ['terms', 'privacy', 'risk', 'fx-risk', 'ndpr'] as const;
type DocId = typeof DOCS[number];

const DOC_TITLES: Record<DocId, string> = {
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
  risk: 'Risk Disclosure',
  'fx-risk': 'FX Risk Disclosure',
  ndpr: 'NDPR Compliance',
};

const DOC_BODIES: Record<DocId, string> = {
  terms: `NigaStock is operated by Nigastock Technologies Limited (RC 1,902,345). We provide an electronic trading terminal and broker-routing layer on top of SEC-licensed Nigerian brokers. NigaStock itself is NOT a licensed broker/dealer and does not custody your funds or securities.

Eligibility: you must be at least 18, resident in Nigeria (or entitled to trade NGX), and pass KYC (BVN, NIN, government ID).

Broker integrations: orders are routed to the broker you pick (Bamboo, Chapel Hill Denham, Meristem, Cordros, or additional brokers we onboard). You may delegate to the Smart Order Router (SOR) for best-execution selection.

Fees: NigaStock charges a Terminal Fee (currently 0.25% of consideration) on top of the standard NGX schedule (Brokerage, SEC Fee, CSCS Fee, Stamp Duty on buys, plus VAT). Every fee is surfaced before submission.

Prohibited conduct: no market-manipulative activity (spoofing, wash trading, layering), no unauthorised automation, no sharing of credentials, no money laundering or sanctions evasion.

Security: Cognito-backed authentication with MFA enforced for trading actions (SEC 2018 Rule 10(3)). Session tokens live in browser memory only.

Data & audit: all order placements, modifications, and cancellations are persisted to S3 Object-Lock with a 7-year retention period (SEC Rule 30.13). See Privacy Policy and NDPR Compliance for more.

Liability: NigaStock is provided "as is"; aggregate liability is capped at Terminal Fees paid in the preceding three months. See Risk Disclosure.

Regulation & disputes: governed by Nigerian law, arbitration in Lagos under LCA rules.

By proceeding you confirm you have read, understood, and accept these Terms.`,

  privacy: `We collect Identity data (name, DOB, BVN, NIN, ID image, address), Financial data (bank, linked brokers, trading history), Device data (user-agent, IP, session logs), and Usage data (pages, symbols watched, orders).

Lawful bases under NDPA 2023 / NDPR 2019: Contract (to deliver the service), Legal Obligation (SEC/CSCS record-keeping, AML), Legitimate Interest (fraud prevention), and Consent (marketing, analytics cookies).

We use your data to route orders, perform KYC/AML, authenticate sessions (Cognito + MFA), reconcile fills, audit trades, and surface personalised insights. We never sell your data.

Storage: AWS eu-west-1, SOC 2 controls, KMS AES-256 at rest, TLS 1.3 in transit. Trading records retained 7 years; session logs 90 days; marketing opt-ins until revoked.

Sharing: the broker you select (for execution) and regulators (SEC, CSCS, NIBSS when required). No marketing-list sharing.

Cookies: strictly-necessary only by default; analytics are opt-in. No third-party ad trackers.

Your NDPR rights: Access, Rectification, Erasure (subject to retention), Portability, Objection, Complaint. Exercise via dpo@nigastock.ng.

International transfers: to AWS eu-west-1 under NDPC-approved Standard Contractual Clauses.

Children: service not offered to under-18s. Sanctions screening monthly against OFAC/UN/EU/UK lists.

By proceeding you confirm you have read and understood our data-handling practices.`,

  risk: `IMPORTANT: Investing involves risk. You can lose some or all of the money you invest. Past performance is not indicative of future results. Do not invest funds you cannot afford to lose.

General market risk: prices fluctuate on macro, sector, and corporate events. Equities may suspend, see wide spreads, or stop paying dividends.

Bond risks: interest-rate risk (duration), credit risk (issuer default — FGN lowest, corporate/sub-sovereign higher), liquidity risk (thin secondary markets), call/prepayment risk (early redemption when rates fall).

ETF risks: premium/discount to NAV, tracking error, Total Expense Ratio compounding over time, commodity-backed ETFs exposed to underlying + FX.

Liquidity & settlement: limit orders may remain working indefinitely. T+2 settlement for NGX equities/ETFs; sales proceeds unavailable until settled. CSCS cut-off differs on holidays.

Counterparty risk: the linked broker holds securities at CSCS and is the counterparty. Broker insolvency is rare but possible; SEC Investor Protection Fund may cover part of a loss.

Technology risk: connectivity, broker APIs, NGX matching-engine outages, or NigaStock maintenance can delay or prevent order placement/cancellation/modification. The WebSocket kill mechanism closes working orders on connection loss but is not a substitute for active monitoring.

FX & sovereign risk: NGN devaluation, capital controls, and CBN policy changes affect real returns. Sovereign risk on FGN bonds includes re-denomination, restructuring, or haircut events.

Algorithmic recommendations: the BUY/HOLD/SELL traffic-light signals are NOT investment advice. They are computed from technical indicators and published fundamentals and do not account for your personal circumstances.

By proceeding you acknowledge you have read and understood these risks and that you are making your own investment decisions.`,

  ndpr: `Controller: Nigastock Technologies Limited (RC 1,902,345), 24 Adeola Odeku St, Victoria Island, Lagos.
Processors: AWS EMEA SARL (infrastructure), Cognito (auth), your linked broker (execution).
DPO: Chuka Okonkwo, CIPP/E — dpo@nigastock.ng.
DPCO: DataProtection Partners Ltd (NDPC-licensed). Audit reports filed annually.

Principles: lawfulness & fairness, purpose limitation, data minimisation, accuracy, storage limitation, integrity & confidentiality, accountability. Each mapped to a technical control in our Data Protection Management Programme (DPMP).

Privacy by design & default: tokens in memory only (never localStorage), strict CSP, MFA for trading, least-privilege IAM with resource-scoped policies, KMS AES-256 at rest, TLS 1.3 in transit, quarterly DPIA for any new personal-data processing.

Records of Processing Activities (RoPA): live register of every data flow — category, purpose, lawful basis, retention, recipients, transfer safeguards. Reviewed every six months.

Subject Access Requests: handled via dpo@nigastock.ng within 30 days (extendable 60 days for complex cases). Identity verification via BVN + phone OTP. First SAR per year is free.

Breach notification: to NDPC within 72 hours; to affected subjects without delay when risk to rights is high. Annual tabletop exercises.

International transfers: to AWS eu-west-1 (Ireland) under NDPC-approved adequacy framework + Standard Contractual Clauses. No transfers to non-adequate third countries.

Vendor management: every processor signs a DPA before onboarding. SOC 2 / ISO 27001 evidence reviewed annually. Breach-notification SLAs required.

Training: 4-hour NDPR onboarding for all staff + 1-hour refresher annually. Trading-support staff also complete SEC electronic-records training.

Audits: annual NDPR audit by our DPCO. SOC 2 Type II in progress (target Q3 2026). ISO 27001 planning underway.

By proceeding you confirm you have read this compliance statement.`,

  'fx-risk': `IMPORTANT: Trading foreign exchange (FX) carries a high level of risk. Currency prices can move sharply against you in seconds. Leverage amplifies both gains and losses. You can lose the entire amount you have deposited, and in some circumstances more. Do not trade FX with funds you cannot afford to lose.

Scope: this covers spot FX on USD/NGN and the African pair complex (Tier-A: ZAR, EGP, KES, MAD, GHS; Tier-B: XOF, XAF, ZMW, BWP, UGX, TZS, RWF, TND, DZD; Tier-C: ETB, AOA reference-only) and synthetically-constructed Africa-Africa crosses (NGN/ZAR, NGN/KES, ZAR/KES) constructed via USD or EUR bridges. NigaStock is NOT an FX dealer and does not act as principal counterparty.

FX trading is offered only where, and to the extent, FX is made available by the authorities — the Central Bank of Nigeria (CBN), the Nigerian SEC, and FMDQ. The supported pair complex, leverage caps, synthetic-cross methodology, and supported execution venues may be amended, suspended, or withdrawn in response to changes in CBN circulars, SEC rules, or FMDQ NAFEM operating model.

Leverage & margin: a small deposit controls a much larger notional. A 1% adverse move on a 50:1 leveraged position wipes out 50% of margin. Margin calls and auto-liquidation may close positions without further notice; liquidation prices may be materially worse than displayed quotes during fast markets.

Spread, slippage & last-look: the headline USD/NGN spread is 22 pips under normal conditions; African-complex pairs and synthetic crosses carry per-pair overrides plus an additional spread component. Spreads are NOT contractually fixed — during news, low-liquidity sessions, CBN intervention windows, or rate decisions they can widen to several multiples. Market orders fill at the next available price; slippage on stops and gaps is your risk. Some venues may reject orders within a last-look window.

NGN-specific risks: capital controls (CBN restrictions on cross-border NGN flows), multiple-rate divergences (official, NAFEM, parallel), CBN intervention can move USD/NGN by hundreds of pips in minutes, devaluations may be implemented overnight, and CBN/FMDQ rule changes can alter clearing rules with immediate effect.

Synthetic crosses: NGN is not directly quoted against most African currencies; we construct crosses via USD/EUR bridges. You are simultaneously exposed to two or three USD legs. If the bridge market pauses, the synthetic pauses too. Spreads and slippage on synthetics reflect movement on every leg.

Stops are not guaranteed. A stop-loss becomes a market order once triggered — gapping over weekends, between sessions, or on news can give you a fill materially worse than the stop level. NigaStock does not currently offer guaranteed stops on any FX pair.

Sessions & weekend gaps: FX is a continuous 24/5 market. Liquidity is concentrated in the Lagos overlap with London and New York. Outside those windows spreads widen. Weekend close (Friday NY → Sunday NY) produces the largest gap risk. NAFEM has its own session boundaries; outside the NAFEM window NGN pricing may be reference-only and not tradable.

Execution venues & counterparty risk: FX trades route to a regulated execution venue (paper-trading sandbox during pilot, LMAX Exchange, IS Prime). The venue is your counterparty, not NigaStock. Venue insolvency or operational failure can result in loss of margin and unrealised P&L. Cross-venue netting is not available.

Negative balance: in ordinary conditions our RiskService closes positions before equity goes negative, but in gap or fast-market conditions this may not be possible. Negative-balance protection depends on the execution venue's policy. NigaStock does not advance funds to cover client deficits.

Technology: WebSocket disconnects do not auto-close positions. The pricing-engine kill-switch can pause quoting (preventing new orders) but does not close existing positions. Mobile networks on Nigerian carriers are not a reliable substrate for active position management.

Pricing-engine & signals disclaimer: displayed bid/ask are indicative; executable price at fill may differ. The 22-pip headline spread is a configuration value and may be changed without notice. Any BUY/HOLD/SELL signals or technical-analysis tools are NOT investment advice and do not account for your personal circumstances.

Tax & suitability: FX gains and losses may be taxable in Nigeria and any other jurisdiction where you are resident. NigaStock does not provide tax advice. FX trading is not suitable for all investors; the KYC tier you hold and the suitability declarations you sign limit the products you can trade and the leverage you can apply.

By proceeding you acknowledge: (a) you have read and understood this disclosure in full; (b) you understand FX trading is high-risk and you can lose everything you deposit (and possibly more without negative-balance protection); (c) NigaStock is the technology and routing layer, and counterparty risk runs to your selected execution venue; (d) the funds you commit to FX are funds you can afford to lose without affecting your wellbeing.`,
};

function loadState(): Record<DocId, string> {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    if (!raw) return {} as Record<DocId, string>;
    return JSON.parse(raw);
  } catch { return {} as Record<DocId, string>; }
}
function saveState(s: Record<DocId, string>) {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch {}
}
export function hasAcceptedAll(): boolean {
  const s = loadState();
  return DOCS.every((d) => !!s[d]);
}

interface Props { onComplete: () => void }

export function LegalGate({ onComplete }: Props) {
  const [state, setState] = useState<Record<DocId, string>>(() => loadState());
  const [active, setActive] = useState<DocId>(DOCS.find((d) => !state[d]) ?? 'terms');
  const [scrolled, setScrolled] = useState<Record<DocId, boolean>>({} as any);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Reset scroll position + re-evaluate scroll gate whenever the tab changes.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [active]);

  const onScroll = () => {
    const el = bodyRef.current; if (!el) return;
    const reached = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (reached && !scrolled[active]) setScrolled((s) => ({ ...s, [active]: true }));
  };
  const canAccept = scrolled[active] || !!state[active];

  const accept = () => {
    const next = { ...state, [active]: new Date().toISOString() };
    setState(next); saveState(next);
    const remaining = DOCS.find((d) => !next[d]);
    if (remaining) setActive(remaining);
    else onComplete();
  };

  const done = DOCS.filter((d) => !!state[d]).length;

  return (
    <div className="lg-backdrop" role="dialog" aria-modal="true" aria-label="Legal acknowledgements">
      <div className="lg-card">
        <div className="lg-head">
          <div>
            <strong>Review &amp; acknowledge</strong>
            <div className="lg-sub">Scroll each document to the end, then acknowledge. {done}/5 complete.</div>
          </div>
        </div>
        <div className="lg-tabs" role="tablist">
          {DOCS.map((d, i) => (
            <button key={d} className={'lg-tab ' + (d === active ? 'active' : '') + (state[d] ? ' done' : '')}
              onClick={() => setActive(d)}
              role="tab" aria-selected={d === active}>
              <span className="lg-tab-n">{i + 1}.</span> {DOC_TITLES[d]}
              {state[d] && <span className="lg-check">&#x2713;</span>}
            </button>
          ))}
        </div>
        <div className="lg-body" ref={bodyRef} onScroll={onScroll} role="document">
          <h2 className="lg-body-h">{DOC_TITLES[active]}</h2>
          {DOC_BODIES[active].split('\n\n').map((para, k) => (
            <p key={k}>{para}</p>
          ))}
          <div className="lg-body-end">End of {DOC_TITLES[active]}.</div>
        </div>
        <div className="lg-foot">
          <div className="lg-progress"><span style={{ width: (done * 20) + '%' }} /></div>
          <button
            className={'lg-accept ' + (canAccept ? 'enabled' : '')}
            disabled={!canAccept}
            onClick={accept}
          >{state[active] ? 'Already acknowledged' : canAccept ? 'I have read and agree' : 'Scroll to the end to enable'}</button>
        </div>
      </div>
    </div>
  );
}
